import { Controller } from '@hotwired/stimulus';
import { Offcanvas } from 'bootstrap';

const ACTIVATION_OFFSET_PX = 24;
export default class SidebarNavigationController extends Controller {
  static targets = ['link', 'section', 'toggleButton', 'offcanvas', 'backToTop'];

  activeSectionId = null;
  pendingSectionId = null;
  ticking = false;

  connect() {
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll, { passive: true });
    window.addEventListener('scrollend', this.onScrollEnd);
    this.refreshActiveSection();
  }

  disconnect() {
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onScroll);
    window.removeEventListener('scrollend', this.onScrollEnd);
    this.pendingSectionId = null;
  }

  offcanvasTargetConnected(element) {
    element.addEventListener('show.bs.offcanvas', this.onShow);
    element.addEventListener('hidden.bs.offcanvas', this.onHidden);
  }

  offcanvasTargetDisconnected(element) {
    element.removeEventListener('show.bs.offcanvas', this.onShow);
    element.removeEventListener('hidden.bs.offcanvas', this.onHidden);
  }

  setVisibility(hidden) {
    if (this.hasToggleButtonTarget) {
      this.toggleButtonTarget.classList.toggle('d-none', hidden);
    }
    if (this.hasBackToTopTarget) {
      this.backToTopTarget.classList.toggle('d-none', hidden);
    }
  }

  onShow = () => {
    this.setVisibility(true);
  };

  onHidden = () => {
    this.setVisibility(false);
  };

  onScroll = () => {
    this.scheduleActiveSectionRefresh();
  };

  onScrollEnd = () => {
    this.pendingSectionId = null;
    this.scheduleActiveSectionRefresh();
  };

  selectSection(event) {
    const sectionId = event.currentTarget.dataset.sectionId;

    this.activeSectionId = sectionId;
    this.pendingSectionId = this.computeActiveSectionId() === sectionId ? null : sectionId;
    this.highlightLink();

    if (this.hasOffcanvasTarget) {
      Offcanvas.getInstance(this.offcanvasTarget)?.hide();
    }
  }

  scheduleActiveSectionRefresh() {
    if (this.ticking) {
      return;
    }

    this.ticking = true;
    requestAnimationFrame(() => {
      this.ticking = false;
      this.refreshActiveSection();
    });
  }

  refreshActiveSection() {
    const computedActiveSectionId = this.computeActiveSectionId();

    if (this.pendingSectionId !== null) {
      if (computedActiveSectionId !== this.pendingSectionId) {
        return;
      }
      this.pendingSectionId = null;
    }

    this.activeSectionId = computedActiveSectionId;
    this.highlightLink();
  }

  computeActiveSectionId() {
    let activeSectionId = null;

    for (const section of this.linkedSections()) {
      if (section.getBoundingClientRect().top > ACTIVATION_OFFSET_PX) {
        break;
      }
      activeSectionId = section.id;
    }

    return activeSectionId;
  }

  linkedSections() {
    const linkedIds = new Set(this.linkTargets.map((link) => link.dataset.sectionId));

    return this.sectionTargets.filter((section) => section.id && linkedIds.has(section.id));
  }

  highlightLink() {
    this.linkTargets.forEach((link) => {
      const isActive = this.activeSectionId !== null && link.dataset.sectionId === this.activeSectionId;

      link.classList.toggle('text-primary', isActive);
      link.classList.toggle('bg-primary-subtle', isActive);
      link.classList.toggle('fw-bold', isActive);
      link.classList.toggle('text-black', !isActive);
    });
  }
}
